const isProd = process.env.NODE_ENV === "production";

/**
 * Transforma recursivamente un ítem del menú del backend al formato del frontend.
 * Backend: { etiqueta, icono, ruta, hijos, borrado, orden }
 * Frontend: { name, icon, path, children }
 */
function TransformaItem(item) {
  var hijos = [];
  if (item.hijos && item.hijos.length > 0) {
    hijos = item.hijos
      .filter(function (h) { return !h.borrado; })
      .sort(function (a, b) { return a.orden - b.orden; })
      .map(TransformaItem);
  }
  var resultado = {
    name: item.etiqueta,
    icon: item.icono || "",
    path: item.ruta,
  };
  if (hijos.length > 0) {
    resultado.children = hijos;
  }
  return resultado;
}

/**
 * Rutas raíz del autoservicio. Se comparan normalizadas (igual que
 * MenuFiltraPorPlan en el backend, que empata por `Ruta` normalizada) porque el
 * menú vive capturado a mano en la base y los ítems no traen ID sembrado.
 *
 * No se incluye "/" a propósito: en el menú de Colaborador el autoservicio
 * cuelga de la raíz, y "/" es también el destino de Default.vue, que ya redirige
 * según el perfil. Filtrar "/" escondería el único ítem de ese menú.
 */
const RutasAutoservicio = ["/autoservicio", "/empleado", "/main/empleado"];

function EsRutaAutoservicio(Ruta) {
  if (Ruta == null) return false;
  var R = String(Ruta).trim().toLowerCase();
  if (R === "") return false;
  return RutasAutoservicio.some(function (Raiz) {
    return R === Raiz || R.indexOf(Raiz + "/") === 0;
  });
}

export default {
  /**
   * Transforma la respuesta del backend de menú al formato que usa el sidebar.
   * @param {Object} menuBackend - Respuesta del API { etiqueta, menu: [...] }
   * @returns {Array} Array de ítems de menú en formato { name, icon, path, children }
   */
  TransformaMenuBackend(menuBackend) {
    if (!menuBackend || !menuBackend.menu || !Array.isArray(menuBackend.menu)) {
      return [];
    }
    return menuBackend.menu
      .filter(function (item) { return !item.borrado; })
      .sort(function (a, b) { return a.orden - b.orden; })
      .map(TransformaItem);
  },

  /**
   * Quita del menú las opciones de autoservicio cuando el usuario no está dado
   * de alta como colaborador en la suscripción seleccionada.
   *
   * El autoservicio es personal. Un SuperAdministrador que abre una cuenta ajena
   * no tiene colaborador ahí, así que la API ya le responde 404 (eClock6,
   * BaseController.ObtenColaboradorID); esto sólo evita ofrecerle la opción.
   * Se aplica tanto al menú del backend como al mínimo, porque el perfil no
   * sirve para decidirlo: el front trata a un superadmin como "Administrador".
   *
   * @param {Array} Menu Ítems { name, icon, path, children }.
   * @param {boolean} TieneColaborador Si el usuario tiene ColaboradorID propio.
   * @returns {Array} El menú sin las opciones de autoservicio.
   */
  FiltraAutoservicio(Menu, TieneColaborador) {
    if (TieneColaborador) return Menu;
    if (!Array.isArray(Menu)) return Menu;

    var Filtra = function (Items) {
      return Items.filter(function (Item) {
        return !EsRutaAutoservicio(Item.path);
      }).map(function (Item) {
        if (!Item.children || Item.children.length === 0) return Item;
        var Hijos = Filtra(Item.children);
        return Object.assign({}, Item, { children: Hijos });
      }).filter(function (Item) {
        //Un padre que se quedó sin hijos ya no navega a ningún lado.
        return !Item.children || Item.children.length > 0;
      });
    };

    return Filtra(Menu);
  },

  /**
   * Menú mínimo, para cuando el backend no pudo entregar el suyo.
   *
   * Sólo Home y Autoservicio: las dos únicas opciones que existen en todos los
   * planes, incluidos los acotados como NOM035. Un colaborador ni siquiera ve
   * Home. Desde aquí el usuario puede recargar; lo que no puede es entrar a
   * módulos que quizá no contrató.
   *
   * @param {string} PerfilID Perfil del usuario en la suscripción.
   * @returns {Array} Ítems de menú en formato { name, icon, path }.
   */
  ObtenMenuMinimo(PerfilID) {
    if (PerfilID === "Colaborador") {
      return [
        {
          name: "Autoservicio",
          icon: "fas fa-house-user",
          path: "/",
        },
      ];
    }
    return [
      {
        name: "Home",
        icon: "tim-icons icon-chart-pie-36",
        path: "/main/home",
      },
      {
        name: "Autoservicio",
        icon: "fas fa-house-user",
        path: "/autoservicio",
      },
    ];
  },

  // TODO(entitlements): los ObtenMenu* de abajo son el menú estático anterior al
  // menú por backend (GET /api/Suscripciones/{id}/Usuarios/Menu). No saben nada
  // del plan de la suscripción: ObtenMenuAdministrador lista los 12 módulos sin
  // condición alguna. Ya no se usan como respaldo de la carga del menú — ese
  // camino ahora va a ObtenMenuMinimo — pero siguen aquí porque son la única
  // descripción del árbol completo que existe en el repo: no hay semilla del
  // menú predeterminado ni en el front ni en la API, el menú vive capturado en
  // la propiedad MenuPredeterminado-{Perfil} de la base. Al sembrar el menú
  // desde código, esta lista es el punto de partida; mientras tanto, no
  // conectarlos a ningún camino de respaldo.
  // Ver docs/NOM035.Entitlements.Analisis.md.
  ObtenMenuAdministrador(EsEmbajador) {
    var R = [];
    R.push({
      name: "Home",
      icon: "tim-icons icon-chart-pie-36",
      //<i class="fas fa-user-clock"></i>
      path: "/main/home",
    });
    R.push({
      name: "Autoservicio",
      icon: "fas fa-house-user",
      path: "/autoservicio",
    });
    // R.push({
    //   name: "Anuncios",
    //   icon: "fas fa-bullhorn",
    //   path: "/clientes",
    // });
    var Personal = {
      name: "Personal",
      icon: "fas fa-address-book",
      path: "/personal",
      children: [
        {
          name: "Listado",
          path: "/personal",
        },
        {
          name: "Autoregistro",
          path: "/auto-registro",
        },
        { 
          name: "Organigrama",
          path: "/organigrama",
        },
        {
          name: "Departamentos",
          path: "/departamentos",
        },
        {
          name: "Puestos",
          path: "/puestos",
        },
        {
          name: "Empresas",
          path: "/empresas",
        },
        {
          name: "Centros de Costos",
          path: "/centroscostos",
        },
        {
          name: "Grupos",
          path: "/grupos",
        },
        {
          name: "Divisiones",
          path: "/divisiones",
        },
        {
          name: "Gestiones",
          path: "/gestiones",
        },
      ],
    };
    /*  if (!isProd) {
        Personal.children.push({
          name: 'NOM-035-STPS-2018',
          path: '/nom035stps2018'
        });
      }*/
    R.push(Personal);
    //<i class="fas fa-brain"></i>
    R.push({
      name: "Solicitudes",
      icon: "fas fa-user-check",
      path: "/solicitudes",
    });

    R.push({
      name: "Checadas",
      icon: "fas fa-door-open",
      path: "/accesos",
      children: [
        {
          name: "Listado",
          path: "/accesos",
        },
        {
          name: "Modo Foto",
          path: "/accesos/modo-foto",
        },
        {
          name: "Comedor",
          path: "/accesos/comedor",
        },
      ],
    });

    R.push({
      name: "Asistencias",
      icon: "tim-icons icon-time-alarm",
      path: "/asistencias",
      children: [
        {
          name: "Listado",
          path: "/asistencias",
        },

        {
          name: "Turnos",
          path: "/turnos",
        },
        {
          name: "Dias Festivos",
          path: "/diasfestivos",
        },
        {
          name: "Tipos de incidencias",
          icon: "tim-icons icon-time-alarm",
          path: "/tiposincidencias",
        },
        {
          name: "Reglas de Vacaciones",
          path: "/reglasVacaciones",
        },
        {
          name: "Periodos de nóminas",
          path: "/periodosnominas",
        },
        {
          name: "Prenominas",
          path: "/prenominas",
        },
        {
          name: "Cargos",
          path: "/cargos",
        },
        {
          name: "Indicadores",
          path: "/indicadoresAsistencias",
        },
        {
          name: "Eventos",
          path: "/eventos",
        },
      ],
    });

    R.push({
      name: "Radar",
      icon: "fas fa-satellite-dish",
      path: "/radar",
    });

    var Evaluaciones = {
      name: "Evaluaciones",
      icon: "fas fa-brain",
      path: "/evaluaciones",
      children: [
        {
          name: "NOM-035-STPS-2018",
          path: "/nom035stps2018",
        },
        {
          name: "Talento y Mejora",
          path: "/revision-mejora",
        },
        {
          name: "Asistencia",
          path: "/asistencias/metricas",
        },
        {
          name: "Crecimiento Salarial",
          path: "/sueldos",
        },
      ],
    };
    R.push(Evaluaciones);

    R.push({
      name: "Dispositivos",
      icon: "fas fa-fingerprint",
      path: "/dispositivos",
    });

    R.push({
      name: "Ubicaciones",
      icon: "fas fa-map-marked",
      path: "/asistencias",
      children: [
        {
          name: "Listado",
          path: "/ubicaciones",
        },
        {
          name: "Regiones",
          path: "/regiones",
        },
        {
          name: "Personal",
          path: "/accesos/mapa",
        },
        {
          name: "Rondines",
          path: "/rondines",
        },
      ],
    });

    var Ls = localStorage.perfilLocal;
    if (Ls != null && Ls != "") {
      var PerfilLocal = JSON.parse(Ls);
      if (PerfilLocal.usuario.esSuperAdministrador) {
        R.push({
          name: "Suscripciones",
          icon: "fas fa-credit-card",
          path: "/suscripciones",
          children: [
            {
              name: "Listado",
              path: "/suscripciones",
            },
            {
              name: "Métricas SaaS",
              path: "/suscripciones/metricasSaaS",
            },
            {
              name: "Cálculo de CAC",
              path: "/suscripciones/calculoCAC",
            },
          ],
        });
      }
    }

    /*  R.push({
        name: 'Monitor de Actividad',
        icon: 'fas fa-desktop',
        path: '/am'
      });*/
    R.push({
      name: "Aplicaciones",
      icon: "fas fa-cloud",
      path: "/aplicaciones",
    });
 /*   if (EsEmbajador)
      R.push({
        name: "Embajadores",
        icon: "fas fa-bullhorn",
        path: "/embajadores",
      });*/
    // console.debug(R);
    return R;
  },

  ObtenMenuSupervisor(EsEmbajador) {
    var R = [];
    R.push({
      name: "Home",
      icon: "tim-icons icon-chart-pie-36",
      //<i class="fas fa-user-clock"></i>
      path: "/main/home",
    });

    R.push({
      name: "Autoservicio",
      icon: "fas fa-house-user",
      path: "/autoservicio",
    });
    R.push({
      name: "Personal",
      icon: "fas fa-address-book",
      path: "/personal",
    });

    R.push({
      name: "Checadas",
      icon: "fas fa-door-open",
      path: "/accesos",
    });

    R.push({
      name: "Asistencias",
      icon: "tim-icons icon-time-alarm",
      path: "/asistencias",
    });
    R.push({
      name: "Cargos",
      icon: "fas fa-hand-holding-usd",
      path: "/cargos",
    });
    R.push({
      name: "Solicitudes",
      icon: "fas fa-user-check",
      path: "/solicitudes",
    });
    R.push({
      name: "Ubicacion de Personal",
      icon: "fas fa-map-marked",
      path: "/accesos/mapa",
    });

    R.push({
      name: "Radar",
      icon: "fas fa-satellite-dish",
      path: "/radar",
    });

    // console.debug(R);
    return R;
  },
  ObtenMenuColaborador(EsEmbajador) {
    var R = [];

    R.push({
      name: "Autoservicio",
      icon: "fas fa-house-user",
      path: "/",
    });
    // R.push({
    //   name: "Checar",
    //   icon: "fas fa-address-book",
    //   path: "/personal",
    // });

    // R.push({
    //   name: "Checadas",
    //   icon: "fas fa-fingerprint",
    //   path: "/empleado/acceso-nuevo",
    // });

    // R.push({
    //   name: "Puntualidad",
    //   icon: "tim-icons icon-time-alarm",
    //   path: "/empleado/asistencias",
    // });

    // R.push({
    //   name: "Incidencias",
    //   icon: "fas fa-car-crash",
    //   path: "/empleado/incidencias",
    // });
    // R.push({
    //   name: "Vacaciones",
    //   icon: "fas fa-umbrella-beach",
    //   path: "/empleado/vacaciones",
    // });
    //  console.debug(R);
    return R;
    /*        <sidebar-item :link="{
            name: $t('sidebar.mainHome'),
            icon: 'fas fa-home',
            path: '/'
          }">
        </sidebar-item>

        <sidebar-item :link="{
            name: 'Checar',
            icon: 'fas fa-fingerprint',
            path: '/empleado/acceso-nuevo'
          }">
        </sidebar-item>


        <sidebar-item :link="{
            name: 'Puntualidad',
            icon: 'tim-icons icon-time-alarm',
            path: '/empleado/asistencias'
          }">


        </sidebar-item>
        <sidebar-item :link="{
            name: 'Incidencias',
            icon: 'fas fa-car-crash',
            path: '/empleado/incidencias'
          }">
        </sidebar-item>

        <sidebar-item :link="{
            name: 'Vacaciones',
            icon: 'fas fa-umbrella-beach',
            path: '/empleado/vacaciones'
          }">

        </sidebar-item>
        <sidebar-item v-if="MostrarEmbajador" :link="{
            name: 'Embajadores',
            icon: 'fas fa-bullhorn',
            path: '/embajadores'
          }">
        </sidebar-item>*/
  },
  ObtenMenu(PerfilID, EsEmbajador) {
    switch (PerfilID) {
      case "Colaborador":
        return this.ObtenMenuColaborador(EsEmbajador);
      case "Visor":
        return this.ObtenMenuSupervisor(EsEmbajador);
      case "Supervisor":
        return this.ObtenMenuSupervisor(EsEmbajador);
      case "Editor":
        return this.ObtenMenuSupervisor(EsEmbajador);
      case "Administrador":
        return this.ObtenMenuAdministrador(EsEmbajador);
      case "SuperAdministrador":
        return this.ObtenMenuAdministrador(EsEmbajador);
    }

    return [];
  },
};
